import React, { useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { collection, doc, serverTimestamp, writeBatch } from "firebase/firestore";
import { db } from "../firebase";

// ─────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────
const DS = {
  blue:      "#1D6FE8",
  blueSoft:  "#E8F0FD",
  teal:      "#0E8C6A",
  tealSoft:  "#E5F5F0",
  amber:     "#D08B20",
  amberSoft: "#FDF4E3",
  purple:    "#6D3BB5",
  purpleSoft:"#F0EBF8",
  red:       "#C9352A",
  redSoft:   "#FBE9E8",
  muted:     "#64748B",
  border:    "#E2E8F2",
  surface:   "#F8FAFC",
  white:     "#FFFFFF",
  text:      "#0F172A",
  r8:  "8px", r12: "12px", r16: "16px", r20: "20px",
  shadowSm: "0 1px 4px rgba(15,23,42,0.07)",
  shadowMd: "0 4px 16px rgba(15,23,42,0.10)",
};

// ─────────────────────────────────────────────
// UTILITIES  (all logic identical to original)
// ─────────────────────────────────────────────
const emptyMapping = {
  itemNo: "", paymentRef: "", description: "", unit: "",
  qty: "", rate: "", amount: "", sectionNo: "", sectionName: "",
};

function normalizeHeader(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function normalizeItemNo(value) {
  return String(value || "").trim().toUpperCase();
}

function numberOrZero(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const cleaned = String(value || "").replace(/R/gi, "").replace(/,/g, "").trim();
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toSlug(value) {
  return String(value || "").trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function buildSoqItemId(projectId, itemNo) {
  return `soq-${toSlug(projectId)}-${toSlug(itemNo) || "item"}`;
}

function buildBoqSectionId(sectionNo, sectionName) {
  return `boq-${toSlug(sectionNo) || "section"}-${toSlug(sectionName) || "item"}`;
}

function guessColumnMapping(headers) {
  const norm = headers.map((h) => ({ raw: h, normalized: normalizeHeader(h) }));
  const find = (patterns) =>
    norm.find(({ normalized }) => patterns.some((p) => normalized.includes(p)))?.raw || "";
  return {
    itemNo:      find(["item no", "item number", "itemno", "bill item"]),
    paymentRef:  find(["payment refer", "payment ref", "pay ref", "payment reference"]),
    description: find(["description", "item description"]),
    unit:        find(["unit"]),
    qty:         find(["qty", "quantity"]),
    rate:        find(["rate", "tendered rate", "unit rate"]),
    amount:      find(["amount", "value", "tendered amount"]),
    sectionNo:   find(["section no", "section number"]),
    sectionName: find(["section name", "section"]),
  };
}

function extractRowsFromWorksheet(worksheet) {
  const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "", raw: false });
  return Array.isArray(rows) ? rows : [];
}

function fmtCurrency(value) {
  return `R ${numberOrZero(value).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

function fmtNumber(value) {
  return numberOrZero(value).toLocaleString(undefined, { maximumFractionDigits: 4 });
}

// ─────────────────────────────────────────────
// SHARED UI PRIMITIVES
// ─────────────────────────────────────────────
function Btn({ children, onClick, variant = "primary", size = "md", disabled, type = "button", fullWidth }) {
  const base = {
    border: "none", borderRadius: DS.r8, fontWeight: 600, cursor: "pointer",
    fontFamily: "inherit", transition: "all 0.15s", lineHeight: 1,
    padding: size === "sm" ? "6px 12px" : size === "lg" ? "12px 22px" : "9px 16px",
    fontSize: size === "sm" ? 12 : size === "lg" ? 15 : 13,
    opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? "none" : "auto",
    width: fullWidth ? "100%" : undefined,
  };
  const variants = {
    primary:   { background: DS.blue,    color: "#fff" },
    secondary: { background: DS.surface, color: DS.text, border: `1px solid ${DS.border}` },
    success:   { background: DS.teal,    color: "#fff" },
    danger:    { background: DS.red,     color: "#fff" },
    purple:    { background: DS.purple,  color: "#fff" },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{ ...base, ...(variants[variant] || variants.primary) }}>
      {children}
    </button>
  );
}

function SectionCard({ title, subtitle, children, action, accent }) {
  return (
    <div style={{
      background: DS.white, border: `1px solid ${DS.border}`,
      borderRadius: DS.r20, boxShadow: DS.shadowSm, overflow: "hidden", marginBottom: 14,
      ...(accent ? { borderTop: `3px solid ${accent}` } : {}),
    }}>
      <div style={{
        padding: "13px 20px", borderBottom: `1px solid ${DS.border}`,
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: DS.text }}>{title}</h3>
          {subtitle && <p style={{ margin: "2px 0 0", fontSize: 11, color: DS.muted }}>{subtitle}</p>}
        </div>
        {action}
      </div>
      <div style={{ padding: "16px 20px" }}>{children}</div>
    </div>
  );
}

function KpiCard({ label, value, sub, accent, valueColor }) {
  return (
    <div style={{
      background: DS.white, border: `1px solid ${DS.border}`,
      borderRadius: DS.r12, padding: "14px 16px",
      boxShadow: DS.shadowSm, borderTop: `3px solid ${accent || DS.blue}`,
    }}>
      <p style={{ margin: 0, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.08em", color: DS.muted }}>{label}</p>
      <p style={{ margin: "4px 0 0", fontSize: 20, fontWeight: 700,
        letterSpacing: "-0.02em", color: valueColor || DS.text }}>{value}</p>
      {sub && <p style={{ margin: "2px 0 0", fontSize: 11, color: DS.muted }}>{sub}</p>}
    </div>
  );
}

// Shared input / label styles
const inp = {
  width: "100%", boxSizing: "border-box",
  border: `1px solid ${DS.border}`, borderRadius: DS.r8,
  padding: "8px 10px", fontSize: 13, color: DS.text,
  background: DS.white, fontFamily: "inherit",
};
const lbl = {
  display: "block", marginBottom: 4, fontSize: 10, fontWeight: 700,
  textTransform: "uppercase", letterSpacing: "0.07em", color: DS.muted,
};

// Mapping field config — label + whether the field is required
const MAPPING_FIELDS = [
  { key: "itemNo",      label: "Item No",        required: true  },
  { key: "paymentRef",  label: "Payment Ref",    required: false },
  { key: "description", label: "Description",    required: true  },
  { key: "unit",        label: "Unit",           required: true  },
  { key: "qty",         label: "Quantity",       required: true  },
  { key: "rate",        label: "Rate",           required: true  },
  { key: "amount",      label: "Amount",         required: false },
  { key: "sectionNo",   label: "Section No",     required: false },
  { key: "sectionName", label: "Section Name",   required: false },
];

// ─────────────────────────────────────────────
// STEP INDICATOR
// ─────────────────────────────────────────────
const STEPS = ["Select file", "Map columns", "Preview & validate", "Import"];

function StepIndicator({ current }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 20 }}>
      {STEPS.map((label, i) => {
        const done    = i < current;
        const active  = i === current;
        const last    = i === STEPS.length - 1;
        return (
          <React.Fragment key={label}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%", display: "flex",
                alignItems: "center", justifyContent: "center",
                fontWeight: 700, fontSize: 12,
                background: done ? DS.teal : active ? DS.blue : DS.surface,
                color: done || active ? "#fff" : DS.muted,
                border: `2px solid ${done ? DS.teal : active ? DS.blue : DS.border}`,
                transition: "all 0.2s",
              }}>
                {done ? "✓" : i + 1}
              </div>
              <span style={{ fontSize: 10, fontWeight: 600, color: active ? DS.blue : done ? DS.teal : DS.muted,
                whiteSpace: "nowrap" }}>{label}</span>
            </div>
            {!last && (
              <div style={{
                flex: 1, height: 2, margin: "0 6px", marginBottom: 18,
                background: done ? DS.teal : DS.border,
                transition: "background 0.2s",
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────
// COLUMN MAPPING CELL — visual feedback
// ─────────────────────────────────────────────
function MappingField({ fieldKey, label, required, value, onChange, availableHeaders, sampleValue }) {
  const isMapped  = !!value;
  const isError   = required && !isMapped;
  return (
    <div style={{
      background: isError ? DS.redSoft : isMapped ? DS.tealSoft : DS.surface,
      border: `1px solid ${isError ? "#F3C4C1" : isMapped ? "#9FDACB" : DS.border}`,
      borderRadius: DS.r12, padding: "12px 14px",
      transition: "all 0.15s",
    }}>
      <p style={{ margin: "0 0 4px", fontSize: 10, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.07em",
        color: isError ? DS.red : isMapped ? DS.teal : DS.muted }}>
        {label}{required && <span style={{ color: DS.red }}> *</span>}
      </p>
      <select
        value={value || ""}
        onChange={(e) => onChange(fieldKey, e.target.value)}
        style={{
          ...inp, fontSize: 12, padding: "6px 8px",
          background: DS.white,
          borderColor: isError ? "#F3C4C1" : isMapped ? "#9FDACB" : DS.border,
        }}
      >
        <option value="">Not mapped</option>
        {availableHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
      {sampleValue !== undefined && sampleValue !== "" && (
        <p style={{ margin: "5px 0 0", fontSize: 10, color: DS.muted,
          fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          Sample: {String(sampleValue)}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// VALIDATION HELPERS
// ─────────────────────────────────────────────
function getValidationIssues(parsedRows) {
  const issues = [];
  const seenItemNos = new Map();

  parsedRows.forEach((row, i) => {
    if (!row.itemNo) {
      issues.push({ rowIndex: i, type: "error", message: "Empty item number" });
    } else if (seenItemNos.has(row.itemNo)) {
      issues.push({ rowIndex: i, type: "warning",
        message: `Duplicate item no: ${row.itemNo} (first seen at row ${seenItemNos.get(row.itemNo) + 1})` });
    } else {
      seenItemNos.set(row.itemNo, i);
    }

    if (!row.description) {
      issues.push({ rowIndex: i, type: "warning", message: `Row ${i + 1}: empty description` });
    }
    if (!row.unit) {
      issues.push({ rowIndex: i, type: "warning", message: `Row ${i + 1} (${row.itemNo}): no unit` });
    }
    if (row.qty <= 0 && row.rate <= 0) {
      issues.push({ rowIndex: i, type: "warning",
        message: `Row ${i + 1} (${row.itemNo}): qty and rate are both zero` });
    }
  });

  return issues;
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
export default function SOQImportSection({
  currentProjectId,
  currentProject,
  onImportComplete,
}) {
  const fileInputRef = useRef(null);

  const [selectedFileName,  setSelectedFileName]  = useState("");
  const [worksheetNames,    setWorksheetNames]     = useState([]);
  const [selectedSheetName, setSelectedSheetName]  = useState("");
  const [rawRows,           setRawRows]            = useState([]);
  const [availableHeaders,  setAvailableHeaders]   = useState([]);
  const [mapping,           setMapping]            = useState(emptyMapping);
  const [isImporting,       setIsImporting]        = useState(false);
  const [importResult,      setImportResult]       = useState(null); // { success, message, itemCount, sectionCount }
  const [previewLimit,      setPreviewLimit]       = useState(25);
  const [showAllIssues,     setShowAllIssues]      = useState(false);

  // ── Parsed rows ───────────────────────────────
  const parsedRows = useMemo(() => {
    if (!mapping.itemNo) return [];
    return rawRows
      .map((row, index) => {
        const itemNo      = normalizeItemNo(row[mapping.itemNo]);
        const paymentRef  = mapping.paymentRef  ? String(row[mapping.paymentRef]  || "").trim() : "";
        const description = mapping.description ? String(row[mapping.description] || "").trim() : "";
        const unit        = mapping.unit        ? String(row[mapping.unit]        || "").trim() : "";
        const qty         = mapping.qty         ? numberOrZero(row[mapping.qty])         : 0;
        const rate        = mapping.rate        ? numberOrZero(row[mapping.rate])         : 0;
        const amount      = mapping.amount      ? numberOrZero(row[mapping.amount])       : qty * rate;
        const sectionNo   = mapping.sectionNo   ? String(row[mapping.sectionNo]   || "").trim() : "";
        const sectionName = mapping.sectionName ? String(row[mapping.sectionName] || "").trim() : "";
        return { sourceIndex: index, itemNo, paymentRef, description, unit, qty, rate, amount, sectionNo, sectionName };
      })
      .filter((row) => row.itemNo);
  }, [mapping, rawRows]);

  // ── Derived step ──────────────────────────────
  const step = useMemo(() => {
    if (!selectedFileName)               return 0;
    if (!mapping.itemNo)                 return 1;
    if (!parsedRows.length || importResult) return 3;
    return 2;
  }, [selectedFileName, mapping.itemNo, parsedRows, importResult]);

  const previewRows = useMemo(() => parsedRows.slice(0, previewLimit), [parsedRows, previewLimit]);

  const importSummary = useMemo(() => {
    const uniqueSections = new Map();
    parsedRows.forEach((row) => {
      const key = `${row.sectionNo}__${row.sectionName}`;
      if (!uniqueSections.has(key)) uniqueSections.set(key, { sectionNo: row.sectionNo, sectionName: row.sectionName });
    });
    return {
      itemCount:    parsedRows.length,
      sectionCount: uniqueSections.size,
      totalValue:   parsedRows.reduce((s, r) => s + numberOrZero(r.amount), 0),
    };
  }, [parsedRows]);

  const validationIssues = useMemo(() => getValidationIssues(parsedRows), [parsedRows]);
  const errorCount   = validationIssues.filter((i) => i.type === "error").length;
  const warningCount = validationIssues.filter((i) => i.type === "warning").length;

  // Sample values from first data row for mapping preview
  const firstDataRow = rawRows[0] || {};

  // ── File handling ─────────────────────────────
  async function handleFileSelected(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setImportResult(null);
    setSelectedFileName(file.name);

    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const sheetNames = workbook.SheetNames || [];
    setWorksheetNames(sheetNames);

    const firstSheet = sheetNames[0] || "";
    setSelectedSheetName(firstSheet);

    if (!firstSheet) {
      setRawRows([]); setAvailableHeaders([]); setMapping(emptyMapping); return;
    }

    const rows    = extractRowsFromWorksheet(workbook.Sheets[firstSheet]);
    const headers = rows.length ? Object.keys(rows[0]) : [];
    setRawRows(rows);
    setAvailableHeaders(headers);
    setMapping(guessColumnMapping(headers));
    window.__SOQ_IMPORT_WORKBOOK__ = workbook;
  }

  function handleSheetChange(sheetName) {
    setSelectedSheetName(sheetName);
    setImportResult(null);
    const workbook = window.__SOQ_IMPORT_WORKBOOK__;
    if (!workbook || !sheetName) return;
    const rows    = extractRowsFromWorksheet(workbook.Sheets[sheetName]);
    const headers = rows.length ? Object.keys(rows[0]) : [];
    setRawRows(rows);
    setAvailableHeaders(headers);
    setMapping(guessColumnMapping(headers));
  }

  function handleMappingChange(field, value) {
    setMapping((prev) => ({ ...prev, [field]: value }));
    setImportResult(null);
  }

  // ── Import ────────────────────────────────────
  async function handleImport() {
    if (!currentProjectId) {
      setImportResult({ success: false, message: "Select a project before importing the schedule." });
      return;
    }
    if (!mapping.itemNo || !mapping.description || !mapping.unit || !mapping.qty || !mapping.rate) {
      setImportResult({ success: false, message: "Map at least Item No, Description, Unit, Qty and Rate before importing." });
      return;
    }
    if (!parsedRows.length) {
      setImportResult({ success: false, message: "No valid schedule items were found to import." });
      return;
    }
    if (errorCount > 0) {
      setImportResult({ success: false, message: `Fix ${errorCount} error${errorCount !== 1 ? "s" : ""} before importing.` });
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      const batch           = writeBatch(db);
      const uniqueSections  = new Map();

      parsedRows.forEach((row) => {
        const itemId    = buildSoqItemId(currentProjectId, row.itemNo);
        const sectionId = buildBoqSectionId(row.sectionNo, row.sectionName);

        batch.set(
          doc(db, "projects", currentProjectId, "soqItems", itemId),
          {
            id: itemId, projectId: currentProjectId,
            itemNo: row.itemNo, paymentRef: row.paymentRef,
            description: row.description, unit: row.unit,
            tenderQty: row.qty, tenderRate: row.rate,
            tenderAmount: row.amount || row.qty * row.rate,
            sectionNo: row.sectionNo, sectionName: row.sectionName,
            boqSectionId: row.sectionNo || row.sectionName ? sectionId : "",
            sourceSheetName: selectedSheetName, sourceFileName: selectedFileName,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        if (row.sectionNo || row.sectionName) {
          const cur = uniqueSections.get(sectionId) || {
            id: sectionId, projectId: currentProjectId,
            sectionNo: row.sectionNo, sectionName: row.sectionName,
            tenderedAmount: 0, engineersEstimate: 0,
          };
          cur.tenderedAmount    += numberOrZero(row.amount || row.qty * row.rate);
          cur.engineersEstimate += numberOrZero(row.amount || row.qty * row.rate);
          uniqueSections.set(sectionId, cur);
        }
      });

      uniqueSections.forEach((section) => {
        batch.set(
          doc(db, "projects", currentProjectId, "boqSections", section.id),
          { ...section, updatedAt: serverTimestamp() },
          { merge: true }
        );
      });

      // Audit log
      const logRef = doc(collection(db, "projects", currentProjectId, "auditLogs"));
      batch.set(logRef, {
        id: logRef.id, projectId: currentProjectId,
        entityType: "soq_import", entityId: logRef.id, action: "import",
        summary: `Imported ${parsedRows.length} SOQ items from ${selectedFileName}`,
        payload: {
          fileName: selectedFileName, sheetName: selectedSheetName,
          itemCount: parsedRows.length, sectionCount: uniqueSections.size,
        },
        createdAt: serverTimestamp(),
      });

      await batch.commit();

      setImportResult({
        success: true,
        message: `Successfully imported ${parsedRows.length} item${parsedRows.length !== 1 ? "s" : ""} across ${uniqueSections.size} BOQ section${uniqueSections.size !== 1 ? "s" : ""} into project ${currentProjectId}.`,
        itemCount:    parsedRows.length,
        sectionCount: uniqueSections.size,
        totalValue:   importSummary.totalValue,
      });

      if (typeof onImportComplete === "function") {
        onImportComplete({ itemCount: parsedRows.length, sectionCount: uniqueSections.size });
      }
    } catch (error) {
      console.error("SOQ import error:", error);
      setImportResult({ success: false, message: `Import failed: ${error.message || "Check the browser console for details."}` });
    } finally {
      setIsImporting(false);
    }
  }

  // ════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════
  return (
    <div style={{ fontFamily: "'DM Sans','Helvetica Neue',sans-serif" }}>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFileSelected}
        style={{ display: "none" }}
      />

      {/* ── PAGE HEADER ─────────────────────────── */}
      <div style={{
        background: DS.white, border: `1px solid ${DS.border}`, borderRadius: DS.r20,
        boxShadow: DS.shadowSm, padding: "16px 22px", marginBottom: 16,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 12,
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: DS.text,
            letterSpacing: "-0.02em" }}>SOQ Import</h2>
          <p style={{ margin: "3px 0 0", fontSize: 12, color: DS.muted }}>
            Import the master schedule of quantities from Excel, map columns, validate,
            and write each item to Firestore with automatic BOQ section sync.
          </p>
        </div>
        <Btn
          size="lg"
          onClick={() => {
            setImportResult(null);
            fileInputRef.current?.click();
          }}
        >
          {selectedFileName ? "Change File" : "Select Excel File"}
        </Btn>
      </div>

      {/* ── STEP INDICATOR ──────────────────────── */}
      <StepIndicator current={step} />

      {/* ── STEP 0: NO FILE YET ─────────────────── */}
      {!selectedFileName && (
        <div style={{
          background: DS.surface, border: `2px dashed ${DS.border}`,
          borderRadius: DS.r20, padding: "48px 32px", textAlign: "center",
          marginBottom: 16,
        }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📂</div>
          <p style={{ margin: "0 0 6px", fontWeight: 700, fontSize: 15, color: DS.text }}>
            No file selected
          </p>
          <p style={{ margin: "0 0 20px", fontSize: 13, color: DS.muted }}>
            Select an Excel workbook (.xlsx or .xls) containing your schedule of quantities.
            Columns will be auto-detected from the header row.
          </p>
          <Btn size="lg" onClick={() => fileInputRef.current?.click()}>
            Select Excel File
          </Btn>
        </div>
      )}

      {/* ── STEP 1+: SOURCE CARD ─────────────────── */}
      {selectedFileName && (
        <SectionCard
          title="Import Source"
          subtitle="Project, workbook and worksheet selection"
          accent={DS.blue}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            {/* Project */}
            <div>
              <label style={lbl}>Project</label>
              <div style={{
                padding: "8px 12px", borderRadius: DS.r8, fontSize: 13,
                background: currentProjectId ? DS.tealSoft : DS.redSoft,
                border: `1px solid ${currentProjectId ? "#9FDACB" : "#F3C4C1"}`,
                color: currentProjectId ? DS.teal : DS.red,
                fontWeight: 600,
              }}>
                {currentProjectId
                  ? `${currentProjectId} — ${currentProject?.name || ""}`
                  : "No project selected"}
              </div>
            </div>

            {/* File */}
            <div>
              <label style={lbl}>Selected File</label>
              <div style={{
                padding: "8px 12px", borderRadius: DS.r8, fontSize: 13,
                background: DS.blueSoft, border: `1px solid #BDD3F9`,
                color: DS.blue, fontWeight: 600, display: "flex", gap: 8, alignItems: "center",
              }}>
                <span>📄</span>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selectedFileName}
                </span>
              </div>
            </div>

            {/* Worksheet */}
            <div>
              <label style={lbl}>Worksheet</label>
              <select
                style={inp}
                value={selectedSheetName}
                onChange={(e) => handleSheetChange(e.target.value)}
                disabled={!worksheetNames.length}
              >
                <option value="">Select worksheet…</option>
                {worksheetNames.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {worksheetNames.length > 1 && (
                <p style={{ margin: "4px 0 0", fontSize: 10, color: DS.muted }}>
                  {worksheetNames.length} worksheets found in this workbook
                </p>
              )}
            </div>
          </div>

          {/* Raw row count */}
          {rawRows.length > 0 && (
            <div style={{
              marginTop: 12, padding: "8px 12px", borderRadius: DS.r8, fontSize: 11,
              background: DS.surface, border: `1px solid ${DS.border}`,
              color: DS.muted, display: "flex", gap: 8,
            }}>
              <span>ℹ</span>
              <span>{rawRows.length.toLocaleString()} raw rows loaded from <strong>{selectedSheetName}</strong> · {availableHeaders.length} columns detected</span>
            </div>
          )}
        </SectionCard>
      )}

      {/* ── STEP 2: COLUMN MAPPING ───────────────── */}
      {selectedFileName && availableHeaders.length > 0 && (
        <SectionCard
          title="Column Mapping"
          subtitle="Confirm how spreadsheet columns map to SOQ fields. Fields marked * are required."
          accent={DS.purple}
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
            {MAPPING_FIELDS.map(({ key, label, required }) => (
              <MappingField
                key={key}
                fieldKey={key}
                label={label}
                required={required}
                value={mapping[key]}
                onChange={handleMappingChange}
                availableHeaders={availableHeaders}
                sampleValue={mapping[key] ? firstDataRow[mapping[key]] : undefined}
              />
            ))}
          </div>

          {/* Mapping completeness indicator */}
          <div style={{ marginTop: 14 }}>
            {(() => {
              const requiredMapped = MAPPING_FIELDS
                .filter((f) => f.required)
                .every((f) => !!mapping[f.key]);
              const totalMapped = MAPPING_FIELDS.filter((f) => !!mapping[f.key]).length;
              return (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{
                    flex: 1, height: 6, background: DS.border, borderRadius: 99, overflow: "hidden",
                  }}>
                    <div style={{
                      height: "100%", borderRadius: 99, transition: "width 0.3s",
                      width: `${(totalMapped / MAPPING_FIELDS.length) * 100}%`,
                      background: requiredMapped ? DS.teal : DS.amber,
                    }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600,
                    color: requiredMapped ? DS.teal : DS.amber, whiteSpace: "nowrap" }}>
                    {totalMapped} / {MAPPING_FIELDS.length} fields mapped
                    {requiredMapped ? " ✓" : " — required fields missing"}
                  </span>
                </div>
              );
            })()}
          </div>
        </SectionCard>
      )}

      {/* ── STEP 3: SUMMARY + VALIDATION ────────────── */}
      {parsedRows.length > 0 && (
        <>
          {/* KPI row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 14 }}>
            <KpiCard
              label="Items Ready"
              value={importSummary.itemCount.toLocaleString()}
              accent={DS.blue}
              sub={`from ${rawRows.length.toLocaleString()} raw rows`}
            />
            <KpiCard
              label="BOQ Sections"
              value={importSummary.sectionCount}
              accent={DS.purple}
              sub="unique sections detected"
            />
            <KpiCard
              label="Total Tender Value"
              value={fmtCurrency(importSummary.totalValue)}
              accent={DS.teal}
            />
            <KpiCard
              label="Validation"
              value={errorCount > 0 ? `${errorCount} error${errorCount !== 1 ? "s" : ""}` :
                warningCount > 0 ? `${warningCount} warning${warningCount !== 1 ? "s" : ""}` : "All clear"}
              accent={errorCount > 0 ? DS.red : warningCount > 0 ? DS.amber : DS.teal}
              valueColor={errorCount > 0 ? DS.red : warningCount > 0 ? DS.amber : DS.teal}
              sub={errorCount > 0 ? "Fix errors before importing" :
                warningCount > 0 ? "Warnings — review before importing" : "Ready to import"}
            />
          </div>

          {/* Validation issues */}
          {validationIssues.length > 0 && (
            <SectionCard
              title="Validation Issues"
              subtitle={`${errorCount} error${errorCount !== 1 ? "s" : ""} · ${warningCount} warning${warningCount !== 1 ? "s" : ""}`}
              accent={errorCount > 0 ? DS.red : DS.amber}
              action={validationIssues.length > 5 && (
                <Btn size="sm" variant="secondary"
                  onClick={() => setShowAllIssues((v) => !v)}>
                  {showAllIssues ? "Show fewer" : `Show all ${validationIssues.length}`}
                </Btn>
              )}
            >
              <div style={{ display: "grid", gap: 6 }}>
                {(showAllIssues ? validationIssues : validationIssues.slice(0, 5)).map((issue, i) => (
                  <div key={i} style={{
                    display: "flex", gap: 10, alignItems: "flex-start",
                    padding: "8px 12px", borderRadius: DS.r8,
                    background: issue.type === "error" ? DS.redSoft : DS.amberSoft,
                    border: `1px solid ${issue.type === "error" ? "#F3C4C1" : "#F0CFA0"}`,
                  }}>
                    <span style={{ fontSize: 14, flexShrink: 0 }}>
                      {issue.type === "error" ? "✗" : "⚠"}
                    </span>
                    <span style={{
                      fontSize: 12, fontWeight: 600,
                      color: issue.type === "error" ? DS.red : DS.amber,
                    }}>{issue.message}</span>
                  </div>
                ))}
                {!showAllIssues && validationIssues.length > 5 && (
                  <p style={{ margin: 0, fontSize: 11, color: DS.muted }}>
                    + {validationIssues.length - 5} more — click "Show all" to expand
                  </p>
                )}
              </div>
            </SectionCard>
          )}

          {/* Preview table */}
          <SectionCard
            title="Preview"
            subtitle={`Showing ${previewRows.length} of ${parsedRows.length} items`}
            action={
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <label style={{ ...lbl, margin: 0 }}>Rows</label>
                <select
                  value={previewLimit}
                  onChange={(e) => setPreviewLimit(Number(e.target.value))}
                  style={{ ...inp, width: "auto", fontSize: 11, padding: "5px 8px" }}
                >
                  {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            }
          >
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr>
                    {["#","Item No","Payment Ref","Description","Unit","Qty","Rate","Amount","Section No","Section Name"]
                      .map((h) => (
                        <th key={h} style={{
                          padding: "7px 10px", textAlign: h === "#" || ["Qty","Rate","Amount"].includes(h) ? "right" : "left",
                          fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em",
                          color: DS.muted, borderBottom: `2px solid ${DS.border}`,
                          background: DS.surface, whiteSpace: "nowrap",
                        }}>{h}</th>
                      ))
                    }
                  </tr>
                </thead>
                <tbody>
                  {previewRows.length === 0 ? (
                    <tr>
                      <td colSpan={10} style={{ padding: "24px", textAlign: "center", color: DS.muted }}>
                        Select a file and confirm the column mapping to preview schedule items.
                      </td>
                    </tr>
                  ) : previewRows.map((row, i) => {
                    const isDuplicate = validationIssues.some(
                      (issue) => issue.rowIndex === row.sourceIndex && issue.type === "warning" &&
                        issue.message.includes("Duplicate")
                    );
                    return (
                      <tr key={`${row.itemNo}-${row.sourceIndex}`}
                        style={{
                          borderBottom: `1px solid ${DS.border}`,
                          background: isDuplicate ? DS.amberSoft : "transparent",
                        }}
                        onMouseEnter={(e) => !isDuplicate && (e.currentTarget.style.background = DS.surface)}
                        onMouseLeave={(e) => !isDuplicate && (e.currentTarget.style.background = "transparent")}
                      >
                        <td style={{ padding: "7px 10px", textAlign: "right", color: DS.muted }}>{i + 1}</td>
                        <td style={{ padding: "7px 10px", fontWeight: 600, whiteSpace: "nowrap" }}>{row.itemNo}</td>
                        <td style={{ padding: "7px 10px", color: DS.muted }}>{row.paymentRef || "—"}</td>
                        <td style={{ padding: "7px 10px", maxWidth: 280 }}>
                          <span title={row.description}>{row.description}</span>
                        </td>
                        <td style={{ padding: "7px 10px" }}>{row.unit}</td>
                        <td style={{ padding: "7px 10px", textAlign: "right" }}>{fmtNumber(row.qty)}</td>
                        <td style={{ padding: "7px 10px", textAlign: "right" }}>{fmtCurrency(row.rate)}</td>
                        <td style={{ padding: "7px 10px", textAlign: "right", fontWeight: 600 }}>{fmtCurrency(row.amount)}</td>
                        <td style={{ padding: "7px 10px", color: DS.muted }}>{row.sectionNo || "—"}</td>
                        <td style={{ padding: "7px 10px", color: DS.muted }}>{row.sectionName || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
                {previewRows.length > 0 && (
                  <tfoot>
                    <tr style={{ borderTop: `2px solid ${DS.border}` }}>
                      <td colSpan={7} style={{ padding: "8px 10px", textAlign: "right",
                        fontSize: 12, fontWeight: 700, color: DS.text }}>
                        Total ({parsedRows.length} items)
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right",
                        fontSize: 13, fontWeight: 700, color: DS.teal }}>
                        {fmtCurrency(importSummary.totalValue)}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </SectionCard>
        </>
      )}

      {/* ── IMPORT ACTION STRIP ──────────────────── */}
      {selectedFileName && parsedRows.length > 0 && !importResult?.success && (
        <div style={{
          background: DS.white, border: `1px solid ${DS.border}`, borderRadius: DS.r16,
          boxShadow: DS.shadowSm, padding: "16px 20px", marginBottom: 14,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 12,
        }}>
          <div>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: DS.text }}>
              Ready to import {parsedRows.length.toLocaleString()} item{parsedRows.length !== 1 ? "s" : ""}
            </p>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: DS.muted }}>
              {importSummary.sectionCount} section{importSummary.sectionCount !== 1 ? "s" : ""} · {fmtCurrency(importSummary.totalValue)} total · into project {currentProjectId}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            {errorCount > 0 && (
              <div style={{
                padding: "8px 14px", borderRadius: DS.r8, fontSize: 12, fontWeight: 600,
                background: DS.redSoft, color: DS.red, border: `1px solid #F3C4C1`,
              }}>
                ✗ {errorCount} error{errorCount !== 1 ? "s" : ""} must be fixed first
              </div>
            )}
            <Btn
              size="lg"
              variant="success"
              disabled={isImporting || !parsedRows.length || !currentProjectId || errorCount > 0}
              onClick={handleImport}
            >
              {isImporting ? "Importing…" : "Import Schedule"}
            </Btn>
          </div>
        </div>
      )}

      {/* ── RESULT CARD ──────────────────────────── */}
      {importResult && (
        <div style={{
          padding: "20px 24px", borderRadius: DS.r16,
          background: importResult.success ? DS.tealSoft : DS.redSoft,
          border: `1px solid ${importResult.success ? "#9FDACB" : "#F3C4C1"}`,
          marginBottom: 14,
        }}>
          <p style={{ margin: "0 0 6px", fontWeight: 700, fontSize: 15,
            color: importResult.success ? DS.teal : DS.red }}>
            {importResult.success ? "✓ Import successful" : "✗ Import failed"}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: importResult.success ? DS.teal : DS.red }}>
            {importResult.message}
          </p>
          {importResult.success && (
            <div style={{ display: "flex", gap: 16, marginTop: 14 }}>
              <div style={{ background: DS.white, borderRadius: DS.r8, padding: "10px 16px",
                border: `1px solid #9FDACB` }}>
                <p style={{ margin: 0, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.07em", color: DS.muted }}>Items Imported</p>
                <p style={{ margin: "2px 0 0", fontSize: 20, fontWeight: 700,
                  color: DS.teal }}>{importResult.itemCount}</p>
              </div>
              <div style={{ background: DS.white, borderRadius: DS.r8, padding: "10px 16px",
                border: `1px solid #9FDACB` }}>
                <p style={{ margin: 0, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.07em", color: DS.muted }}>BOQ Sections Created / Updated</p>
                <p style={{ margin: "2px 0 0", fontSize: 20, fontWeight: 700,
                  color: DS.teal }}>{importResult.sectionCount}</p>
              </div>
              <div style={{ background: DS.white, borderRadius: DS.r8, padding: "10px 16px",
                border: `1px solid #9FDACB` }}>
                <p style={{ margin: 0, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                  letterSpacing: "0.07em", color: DS.muted }}>Total Value Imported</p>
                <p style={{ margin: "2px 0 0", fontSize: 20, fontWeight: 700,
                  color: DS.teal }}>{fmtCurrency(importResult.totalValue)}</p>
              </div>
            </div>
          )}
          {importResult.success && (
            <div style={{ marginTop: 14, display: "flex", gap: 10 }}>
              <Btn variant="secondary" size="sm" onClick={() => {
                setImportResult(null);
                setSelectedFileName("");
                setRawRows([]);
                setAvailableHeaders([]);
                setMapping(emptyMapping);
                setWorksheetNames([]);
                setSelectedSheetName("");
              }}>
                Import another file
              </Btn>
              {typeof onImportComplete === "function" && (
                <Btn size="sm" onClick={() => onImportComplete({ itemCount: importResult.itemCount, sectionCount: importResult.sectionCount })}>
                  Go to SOQ Register →
                </Btn>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}